import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { UiMessage } from '@/api/inboxTypes';
import type { BotDecision } from '@/api/bots';
import { buildTimeline } from '@/lib/messageModel';
import { canForward } from '@/lib/forward';
import { MessageBubble } from './MessageBubble';

interface Props {
  messages: UiMessage[];
  hasMore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  myUserId: number | null;
  onRetry: (echoId: string) => void;
  onDiscard: (echoId: string) => void;
  /** «Подтвердить / Отклонить» под предложением бота. Ошибку бросает — пузырь вернёт кнопки. */
  onDecide: (invocationId: number, decision: BotDecision) => Promise<void>;
  /** Что показать, когда сообщений нет (первое ещё не пришло). */
  empty: React.ReactNode;
  /** Режим выбора для пересылки: null — обычный. */
  selected?: ReadonlySet<number> | null;
  onLongPress?: (id: number) => void;
  onToggle?: (id: number) => void;
}

const NEAR_BOTTOM = 140;
const NEAR_TOP = 80;

/**
 * Лента сообщений. Прокрутка:
 *  • при открытии — вниз;
 *  • новое сообщение — вниз, если человек и так у низа или это его собственное;
 *  • старые подгружаются при прокрутке вверх, при этом положение «где читал» сохраняется.
 */
export function MessageList({ messages, hasMore, loadingOlder, onLoadOlder, myUserId, onRetry, onDiscard, onDecide, empty, selected, onLongPress, onToggle }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const stuck = useRef(true);
  const anchor = useRef<{ height: number; top: number } | null>(null);
  const first = useRef(true);
  const timeline = useMemo(() => buildTimeline(messages), [messages]);
  const lastMessage = messages[messages.length - 1];
  const lastKey = lastMessage ? `${lastMessage.id}:${lastMessage.local ?? ''}` : '';

  // После подгрузки старых — вернуть то место, где читал.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    if (anchor.current) {
      el.scrollTop = anchor.current.top + (el.scrollHeight - anchor.current.height);
      anchor.current = null;
      return;
    }
    if (first.current && messages.length > 0) {
      el.scrollTop = el.scrollHeight;
      first.current = false;
      return;
    }
    if (stuck.current || lastMessage?.local) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Картинки и голосовые догружаются и растят ленту — если человек у низа, остаёмся у низа.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (stuck.current) el.scrollTop = el.scrollHeight; });
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);

  const loadOlder = () => {
    const el = box.current;
    if (!el || loadingOlder || !hasMore) return;
    anchor.current = { height: el.scrollHeight, top: el.scrollTop };
    onLoadOlder();
  };

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    stuck.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM;
    if (el.scrollTop < NEAR_TOP) loadOlder();
  };

  return (
    <div className="scr" ref={box} onScroll={onScroll} role="log" aria-label="Сообщения" aria-live="polite">
      {messages.length === 0 ? empty : (
        <div className="msgs">
          {hasMore && (
            <button type="button" className="older" onClick={loadOlder} disabled={loadingOlder}>
              {loadingOlder ? 'Загружаем…' : 'Показать более ранние'}
            </button>
          )}
          {timeline.map((it) => it.type === 'day'
            ? <span key={it.key} className="day">{it.label}</span>
            : <MessageBubble key={it.key} message={it.message} kind={it.kind} myUserId={myUserId} onRetry={onRetry} onDiscard={onDiscard} onDecide={onDecide}
              selectMode={!!selected} selectable={canForward(it.message)} selected={!!selected?.has(it.message.id)}
              onLongPress={onLongPress} onToggle={onToggle} />)}
        </div>
      )}
    </div>
  );
}
